import { useAuthStore } from '@/stores/authStore'
import { saveLastAttemptedRoute } from '@/utils/route'
import { checkRouteAccess, validateSecurityConfiguration } from '@/utils/route-security'
import type { RouteLocationNormalized, Router } from 'vue-router'

/**
 * Setup all router guards for the application
 * Uses centralized security system
 */
export function setupRouterGuards(router: Router) {
  // Validate security configuration on initialization
  const securityValidation = validateSecurityConfiguration()
  if (!securityValidation.valid) {
    console.error('Security configuration errors:', securityValidation.errors)
    throw new Error('Critical security configuration errors detected')
  }

  // Main navigation guard
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()

    try {
      // Prevent redundant navigation
      if (
        to.path === from.path &&
        JSON.stringify(to.query) === JSON.stringify(from.query) &&
        JSON.stringify(to.params) === JSON.stringify(from.params)
      ) {
        return next(false)
      }

      // Wait for auth store readiness only if not ready
      if (!authStore.sessionReady) {
        await authStore.checkAuth()
      }

      // Check access through centralized system
      const accessResult = checkRouteAccess(to, authStore)

      if (accessResult.allowed) {
        return next()
      } else {
        // Save attempted route for redirect after authentication
        if (accessResult.redirectTo?.name === 'login') {
          saveLastAttemptedRoute(to)
        }

        // Check if we're not trying to navigate to the same route
        if (
          accessResult.redirectTo &&
          typeof accessResult.redirectTo === 'object' &&
          'name' in accessResult.redirectTo &&
          accessResult.redirectTo.name === from.name
        ) {
          return next(false)
        }

        return next(accessResult.redirectTo!)
      }
    } catch (error) {
      console.error('Router guard error:', error)

      // Avoid circular redirects
      if (from.name !== 'login') {
        return next({ name: 'login' })
      }

      return next(false)
    }
  })

  // After navigation guard for metadata and UI
  router.afterEach((to, from, failure) => {
    if (failure) {
      // Don't log "Navigation cancelled" errors as they are normal behavior
      if (!failure.message?.includes('Navigation cancelled')) {
        console.error('Navigation failed:', failure)
      }
      return
    }

    // Update page metadata
    updatePageMetadata(to)
  })

  // Navigation error handler
  router.onError((error) => {
    console.error('Router navigation error:', error)

    // Handle specific error types
    if (error.message.includes('Navigation cancelled')) {
      return // User switched before navigation completed
    }

    if (error.message.includes('Loading chunk')) {
      // Code splitting chunk loading error - reload page
      window.location.reload()
      return
    }

    // For other errors redirect to 404
    router.replace({ name: 'not-found' })
  })
}

/**
 * Update page metadata
 */
function updatePageMetadata(to: RouteLocationNormalized) {
  const defaultTitle = 'Mining Pool'
  const routeTitle = to.meta.title as string

  // Update title
  if (routeTitle) {
    document.title = `${routeTitle} | ${defaultTitle}`
  } else {
    document.title = defaultTitle
  }

  // Update description
  const description = to.meta.description as string
  if (description) {
    let metaDescription = document.querySelector('meta[name="description"]')

    if (!metaDescription) {
      metaDescription = document.createElement('meta')
      metaDescription.setAttribute('name', 'description')
      document.head.appendChild(metaDescription)
    }

    metaDescription.setAttribute('content', description)
  }
}
